import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classnames from 'classnames';
import { isEdge } from 'react-device-detect';

import {
  buttonsV2 as Buttons,
  filters as Filters,
  forms as Forms,
} from '@breezeway/brzw-components';

import { COLUMN_SIZES, HEADER_CELL_TYPES } from '../constants';
import { CLASS_NAME as ROW_CLASS_NAME } from '../row/flex_table_row';
import { ORDER_VALUES } from '../../../utils/table';

import './flex_table_header.scss';

const { LEFT } = Buttons.constants.CHILD_ALIGNMENT;
const { LINK_SUBTLE } = Buttons.constants.BUTTON_THEMES;
const { LARGE } = Buttons.constants.BUTTON_SIZES;
const CLASS_NAME = 'flex-table-header';

export class FlexTableHeader extends PureComponent {
  constructor(props) {
    super(props);

    this.onChangeSelected = this.onChangeSelected.bind(this);
    this.onClearAllFilters = this.onClearAllFilters.bind(this);
    this.onClearFilter = this.onClearFilter.bind(this);
    this.onIndeterminateClick = this.onIndeterminateClick.bind(this);
    this.onSetFilter = this.onSetFilter.bind(this);
    this.onSortClick = this.onSortClick.bind(this);
    this.sortIcon = this.sortIcon.bind(this);
  }

  onClearAllFilters(type) {
    const { clearFilters } = this.props;
    clearFilters(type);
  }

  onSetFilter(type, value) {
    const { setFilter } = this.props;
    setFilter(type, value);
  }

  onClearFilter(type, value) {
    const { unsetFilter } = this.props;
    unsetFilter(type, value);
  }

  onSortClick(type) {
    const { changeOrder, order } = this.props;
    const newOrderValue = order === ORDER_VALUES.DESC ? ORDER_VALUES.ASC : ORDER_VALUES.DESC;
    changeOrder(type, newOrderValue);
  }

  onChangeSelected(value) {
    const { onChangeSelected, onSelectAll } = this.props;
    if (value) {
      onSelectAll();
    } else {
      onChangeSelected([]);
    }
  }

  onIndeterminateClick() {
    const { onChangeSelected } = this.props;
    onChangeSelected([]);
  }

  sortIcon(type) {
    const { order, orderBy } = this.props;
    if (type === orderBy) {
      return order === ORDER_VALUES.DESC ? 'caret-down' : 'caret-up';
    } else {
      return null;
    }
  }

  renderNormalCell(data, iterated) {
    const { fixedColumnName } = this.props;

    const width = _.get(data, 'width', COLUMN_SIZES.SM);
    const title = _.get(data, 'title', '');
    const name = _.get(data, 'name', '');
    const sortable = _.get(data, 'sortable', false);

    return (
      <Fragment key={`header-cell-${name}-${iterated}`}>
        <div
          className={classnames(
            `${CLASS_NAME}__header-cell`,
            `${CLASS_NAME}__header-cell--normal`,
            `${ROW_CLASS_NAME}__column`,
            `${ROW_CLASS_NAME}`,
            { '-with-sticky': fixedColumnName === iterated },
          )}
          style={{ width }}
        >
          <Buttons.IconTextButton
            alignment="left"
            className={classnames(`${CLASS_NAME}__button`, {
              [`${CLASS_NAME}__button--sortable`]: sortable,
            })}
            fullWidth
            iconNameRight={sortable ? this.sortIcon(name) : ''}
            onClick={sortable ? () => this.onSortClick(name) : () => {}}
            tag="button"
            theme="link-subtle"
          >
            {title}
          </Buttons.IconTextButton>
        </div>
        {fixedColumnName === iterated && isEdge && (
          <div className={classnames(`${CLASS_NAME}__header-cell`)} style={{ width }} />
        )}
      </Fragment>
    );
  }

  renderFilterCell(data, iterated) {
    const { filters, fixedColumnName } = this.props;

    const name = _.get(data, 'name', '');
    const options = _.get(data, 'options', []);
    const title = _.get(data, 'title', '');
    const searchable = _.get(data, 'searchable', true);
    const width = _.get(data, 'width', COLUMN_SIZES.SM);
    const sortedOptions = _.sortBy(options, option => _.toLower(_.get(option, 'label', '')));

    return (
      <Fragment key={`header-cell-${name}-${iterated}`}>
        <div
          className={classnames(
            `${CLASS_NAME}__header-cell`,
            `${CLASS_NAME}__header-cell--filter`,
            `${ROW_CLASS_NAME}__column`,
            { '-with-sticky': fixedColumnName === iterated },
          )}
          style={{ width }}
        >
          <Filters.Filter
            clearButtonSize={LARGE}
            className={`${CLASS_NAME}__filter`}
            name={title}
            options={sortedOptions}
            selectedOptions={_.get(filters, name)}
            theme="secondary"
            iconButtonOptions={{
              alignment: LEFT,
              className: `${CLASS_NAME}__button`,
              fullWidth: true,
              theme: LINK_SUBTLE,
            }}
            onClear={value => this.onClearFilter(name, value)}
            onClearAll={() => this.onClearAllFilters(name)}
            onSet={value => this.onSetFilter(name, value)}
            searchable={searchable}
          />
        </div>
        {fixedColumnName === iterated && isEdge && (
          <div className={`${CLASS_NAME}__header-cell`} style={{ width }} />
        )}
      </Fragment>
    );
  }

  renderHeaderCell(data, iterated) {
    const cellType = _.get(data, 'type', HEADER_CELL_TYPES.NORMAL);

    switch (cellType) {
      case HEADER_CELL_TYPES.NORMAL:
        return this.renderNormalCell(data, iterated);
      case HEADER_CELL_TYPES.FILTER:
        return this.renderFilterCell(data, iterated);
      default:
        return this.renderNormalCell(data, iterated);
    }
  }

  renderSelectCell() {
    const { indeterminateSelected, allSelected } = this.props;

    return (
      <div className={`${ROW_CLASS_NAME}__column ${ROW_CLASS_NAME}--select-cell`}>
        <Forms.CheckboxInput
          indeterminate={indeterminateSelected}
          name={`select-cell-all-header-${indeterminateSelected}-${allSelected}`}
          onChange={this.onChangeSelected}
          onIndeterminateClick={this.onIndeterminateClick}
          value={allSelected}
        />
      </div>
    );
  }

  render() {
    const { columns, selectable, width } = this.props;
    return (
      <div className={CLASS_NAME} style={{ minWidth: width }}>
        {selectable && this.renderSelectCell()}
        {_.map(columns, (column, iterated) => this.renderHeaderCell(column, iterated))}
      </div>
    );
  }
}

FlexTableHeader.propTypes = {
  allSelected: PropTypes.bool,
  changeOrder: PropTypes.func,
  clearFilters: PropTypes.func,
  columns: PropTypes.shape({}),
  filters: PropTypes.shape({}),
  fixedColumnName: PropTypes.string,
  indeterminateSelected: PropTypes.bool,
  onChangeSelected: PropTypes.func,
  onSelectAll: PropTypes.func,
  order: PropTypes.string,
  orderBy: PropTypes.string,
  selectable: PropTypes.bool,
  setFilter: PropTypes.func,
  unsetFilter: PropTypes.func,
  width: PropTypes.number.isRequired,
};

FlexTableHeader.defaultProps = {
  selectable: false,
};

export default FlexTableHeader;
